/**
 * RouteViewSync — URL is the source of truth for the active workspace.
 *
 * When a logged-in user navigates between dashboards (via the
 * workspace switcher OR by typing a URL OR by a deep link from an
 * email), the persisted `view` in AuthContext can drift from the
 * actual URL. This effect listens to `useLocation()` and calls
 * `setView()` whenever the inferred view differs from the current
 * one — keeping localStorage + UserMenu's active indicator in lockstep
 * with the route.
 *
 * No-op when:
 *   - the user has no available capabilities (unauthed loading)
 *   - the inferred view is already current (avoids render loops)
 *   - the inferred view isn't available for the current user
 *     (AuthContext.setView already warns + ignores in this case)
 *
 * Renders nothing — pure side-effect component, mounted once near
 * the top of the route tree.
 */
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";

function inferViewFromPath(pathname) {
  if (pathname.startsWith("/admin")) return "admin";
  if (pathname.startsWith("/dashboard/provider")) return "provider";
  if (pathname.startsWith("/dashboard/customer")) return "customer";
  return null;
}

export default function RouteViewSync() {
  const { user, view, setView, availableViews } = useAuth();
  const { pathname } = useLocation();

  useEffect(() => {
    if (!user) return;
    const inferred = inferViewFromPath(pathname);
    if (!inferred) return;
    if (inferred === view) return;
    if (!availableViews.includes(inferred)) return;
    setView(inferred);
  }, [pathname, user, view, setView, availableViews]);

  return null;
}
